// USER-LANG-IT-PL-1 L3-FE — the ONE language registry for the web app and the QR kiosk.
//
// PER-HOST SWITCH (owner ruling D4). `.cc` and `.com` are hostnames on ONE deployed app,
// so "German on .cc only" cannot be a build flag. `languageAvailability.json` (next to this
// file) lists, per extra language, the host names where it is ON:
//   { "de": [], "it": [], "pl": [] }                       -> shipped CLOSED: nothing new anywhere
//   { "de": ["app.machine-id.cc"], ... }                   -> L9, .cc release
// sk / en / cs are on every host and are never listed there. The SAME file is read by the
// deploy gate (`node tools/i18n-coverage.js --gate`), which refuses a deploy that switches a
// language on for any host while that language is below 100 % in any namespace (D2) — so a
// host list may only grow in a commit whose locale files are complete.
//
// Store-free and React-free on purpose: core/i18n.ts and the anonymous nameplate page import
// it, and neither may drag Redux or account context in.
import availability from "./languageAvailability.json";

export type SupportedLanguage = "sk" | "en" | "cs" | "de" | "it" | "pl";

/** Menu order for every picker (owner ruling D7). */
export const LANGUAGE_ORDER: readonly SupportedLanguage[] = ["sk", "en", "cs", "de", "it", "pl"];

/** On every host; mirrors ALWAYS_ON_LANGS in tools/i18n-coverage-lib.js. */
export const ALWAYS_ON_LANGUAGES: readonly SupportedLanguage[] = ["sk", "en", "cs"];

/** Each language in its own name — NEVER translated (D7). `Record` keeps the table exhaustive. */
export const LANGUAGE_NATIVE_NAMES: Record<SupportedLanguage, string> = {
  sk: "Slovenčina",
  en: "English",
  cs: "Čeština",
  de: "Deutsch",
  it: "Italiano",
  pl: "Polski",
};

const HOSTS_BY_LANGUAGE: Readonly<Record<string, readonly string[] | undefined>> = availability;

/** Languages switched on for `hostname`, in menu order. */
export const getHostLanguages = (hostname: string): SupportedLanguage[] => {
  const host = hostname.trim().toLowerCase();
  return LANGUAGE_ORDER.filter(
    (lng) =>
      ALWAYS_ON_LANGUAGES.includes(lng) ||
      (HOSTS_BY_LANGUAGE[lng] || []).some((h) => h.trim().toLowerCase() === host)
  );
};

const currentHostname = (): string =>
  typeof window !== "undefined" && window.location ? window.location.hostname : "";

/** This host's languages — what core/i18n.ts passes to i18next as `supportedLngs`. */
export const HOST_LANGUAGES: readonly SupportedLanguage[] = getHostLanguages(currentHostname());

/** True when `lng` is switched on for this host (a profile language may not be — see Layout.tsx). */
export const isHostLanguage = (lng: string): lng is SupportedLanguage =>
  (HOST_LANGUAGES as readonly string[]).includes(lng);
